import { fetchEventSource } from "@microsoft/fetch-event-source";
import { axiosInstance } from "api/instance";
import type { ResumeRequest, SSEEvent, StreamRequest } from "./types";
import {
  appendEvent,
  finishEntry,
  startResumeEntry,
  startStreamEntry,
} from "./debugLog";

const AGENT_API_HOST = process.env.REACT_APP_AGENT_API_HOST || "";

// 에이전트가 /library 로 다른 활동지를 조회할 때 cowork 백엔드가 메인 백엔드로
// 그대로 forward 할 사용자 토큰. 메인 백엔드와 동일하게 X-Auth-Token 으로 passthrough.
function authToken(): string | undefined {
  const token = axiosInstance.defaults.headers["X-Auth-Token"];
  return typeof token === "string" && token ? token : undefined;
}

function requireHost(): string {
  if (!AGENT_API_HOST) {
    throw new Error(
      "REACT_APP_AGENT_API_HOST 환경변수가 설정되지 않았어요. .env 에 Agent 서버 호스트를 지정해 주세요."
    );
  }
  return AGENT_API_HOST;
}

export async function* streamCowork(
  req: StreamRequest,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const entryId = startStreamEntry(req);
  yield* withDebugCapture(
    entryId,
    postSSE(`${requireHost()}/cowork/stream`, req, signal)
  );
}

export async function* resumeCowork(
  req: ResumeRequest,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const entryId = startResumeEntry(req);
  yield* withDebugCapture(
    entryId,
    postSSE(`${requireHost()}/cowork/resume`, req, signal)
  );
}

async function* withDebugCapture(
  entryId: string,
  source: AsyncGenerator<SSEEvent>
): AsyncGenerator<SSEEvent> {
  try {
    for await (const event of source) {
      appendEvent(entryId, event);
      yield event;
    }
    finishEntry(entryId, "done");
  } catch (err) {
    finishEntry(
      entryId,
      "error",
      err instanceof Error ? err.message : String(err)
    );
    throw err;
  }
}

// fetch-event-source 는 callback 기반 — AsyncGenerator 로 변환 (queue + Promise resolver)
async function* postSSE(
  url: string,
  body: unknown,
  signal?: AbortSignal
): AsyncGenerator<SSEEvent> {
  const queue: SSEEvent[] = [];
  let resolveNext: (() => void) | null = null;
  let done = false;
  let error: Error | null = null;

  const wakeup = () => {
    const r = resolveNext;
    resolveNext = null;
    r?.();
  };

  // fetch-event-source 는 외부 signal abort 시 promise 를 reject 가 아니라
  // resolve 하고 onclose 도 호출하지 않는다 (lib/cjs/fetch.js). 따라서 abort 만으로는
  // done 이 세팅되지 않아 아래 while 루프가 영원히 park 한다. signal 을 직접 구독해
  // done 을 세팅하고 깨워, abort 시 제너레이터가 확실히 종료되게 한다.
  if (signal) {
    if (signal.aborted) {
      done = true;
    } else {
      signal.addEventListener(
        "abort",
        () => {
          done = true;
          wakeup();
        },
        { once: true }
      );
    }
  }

  const token = authToken();
  const fetchPromise = fetchEventSource(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { "X-Auth-Token": token } : {}),
    },
    body: JSON.stringify(body),
    signal,
    openWhenHidden: true,
    onmessage(msg) {
      if (!msg.event) return;
      try {
        const ev = {
          event: msg.event,
          data: JSON.parse(msg.data),
        } as SSEEvent;
        queue.push(ev);
        wakeup();
      } catch {
        // malformed event — skip
      }
    },
    onerror(err) {
      error = err instanceof Error ? err : new Error(String(err));
      throw error; // 재시도 방지
    },
    onclose() {
      done = true;
      wakeup();
    },
  }).catch((e: unknown) => {
    error = e instanceof Error ? e : new Error(String(e));
    done = true;
    wakeup();
  });

  while (true) {
    while (queue.length) yield queue.shift()!;
    if (done) break;
    await new Promise<void>((r) => (resolveNext = r));
  }
  await fetchPromise;
  if (error) throw error;
}
